/**
 * WebSocket Context for Planning Live-Updates
 *
 * Manages a persistent WebSocket connection to the Django Channels backend.
 * Provides real-time event dispatching for sync updates, scenario changes,
 * conflict alerts, write-back status, sync progress, and notifications.
 *
 * Connection URL: ws(s)://host/ws/planning/?token=<jwt>
 * Protocol: JSON messages with { type, data } structure
 *
 * Features:
 * - Automatic reconnection with exponential backoff (1s → 30s max)
 * - JWT authentication via query parameter
 * - Token refresh on authentication failure (code 4401)
 * - Ping/pong keepalive every 30 seconds
 * - Event subscription system for widget-level handlers
 * - Connection state tracking (connecting, connected, disconnected, reconnecting)
 *
 * @see planning/consumers.py — PlanningConsumer (backend)
 * @see planning/signals.py — emit_* functions (backend)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

// ============================================================================
// Types
// ============================================================================

/** WebSocket connection states */
export type WSConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

/** All event types emitted by the backend PlanningConsumer */
export type WSEventType =
  | 'connection_info'
  | 'sync_update'
  | 'scenario_update'
  | 'conflict_alert'
  | 'writeback_update'
  | 'sync_progress'
  | 'notification'
  | 'pong'
  | 'subscribe_ack';

/** Base message structure from the backend */
export interface WSMessage<T = Record<string, unknown>> {
  type: WSEventType;
  data: T;
}

/** Sync update payload */
export interface SyncUpdateData {
  entity_type: string;
  records_changed: number;
  records_created: number;
  records_updated: number;
  sync_log_id: number;
  timestamp: string;
}

/** Scenario status change payload */
export interface ScenarioUpdateData {
  scenario_id: number;
  scenario_name: string;
  old_status: string;
  new_status: string;
  timestamp: string;
}

/** New conflict detected payload */
export interface ConflictAlertData {
  conflict_id: number;
  conflict_type: string;
  severity: 'info' | 'warning' | 'error';
  description: string;
  affected_operations: number[];
  timestamp: string;
}

/** Write-back completion payload */
export interface WritebackUpdateData {
  writeback_log_id: number;
  scenario_id: number;
  status: 'completed' | 'partial' | 'failed';
  operations_written: number;
  operations_failed: number;
  timestamp: string;
}

/** Live sync progress payload */
export interface SyncProgressData {
  entity_type: string;
  progress_pct: number;
  records_processed: number;
  total_records: number;
  timestamp: string;
}

/** Generic notification payload */
export interface NotificationData {
  level: 'info' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
}

/** Connection info payload (sent on successful connect) */
export interface ConnectionInfoData {
  status: string;
  user: string;
  group: string;
  timestamp: string;
}

/** Event handler callback type */
export type WSEventHandler<T = Record<string, unknown>> = (data: T) => void;

/** Context value exposed to consumers */
export interface WebSocketContextType {
  /** Current connection state */
  connectionState: WSConnectionState;

  /** Whether the WebSocket is connected and authenticated */
  isConnected: boolean;

  /** Last error message, if any */
  lastError: string | null;

  /** Subscribe to a specific event type. Returns unsubscribe function. */
  subscribe: <T = Record<string, unknown>>(
    eventType: WSEventType,
    handler: WSEventHandler<T>
  ) => () => void;

  /** Manually reconnect (e.g., after network recovery) */
  reconnect: () => void;

  /** Recent notifications for display in UI */
  notifications: NotificationData[];

  /** Clear all notifications */
  clearNotifications: () => void;

  /** Connection stats for debugging */
  stats: {
    messagesReceived: number;
    reconnectAttempts: number;
    connectedSince: string | null;
  };
}

// ============================================================================
// Constants
// ============================================================================

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const RECONNECT_BACKOFF_MULTIPLIER = 1.5;
const PING_INTERVAL = 30000;
const MAX_NOTIFICATIONS = 50;
const TOKEN_STORAGE_KEY = 'r4s_omnimens_tokens';

// ============================================================================
// Context
// ============================================================================

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

// ============================================================================
// Helper: Build WebSocket URL
// ============================================================================

function buildWebSocketUrl(token: string, siteCode?: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const path = siteCode
    ? `/ws/planning/site/${siteCode}/`
    : '/ws/planning/';
  return `${protocol}//${host}${path}?token=${encodeURIComponent(token)}`;
}

// ============================================================================
// Provider
// ============================================================================

interface WebSocketProviderProps {
  children: ReactNode;
  /** Optional site code for multi-site filtering (Phase 10.2) */
  siteCode?: string;
  /** Disable auto-connect (useful for testing) */
  disabled?: boolean;
}

export function WebSocketProvider({
  children,
  siteCode,
  disabled = false,
}: WebSocketProviderProps) {
  const { isAuthenticated, getAccessToken, refreshAccessToken } = useAuth();

  // State
  const [connectionState, setConnectionState] =
    useState<WSConnectionState>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [stats, setStats] = useState({
    messagesReceived: 0,
    reconnectAttempts: 0,
    connectedSince: null as string | null,
  });

  // Refs (persist across renders without causing re-renders)
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Map<WSEventType, Set<WSEventHandler<unknown>>>>(
    new Map()
  );
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isUnmountingRef = useRef(false);
  const messageCountRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);

  // ----------------------------------------------------------
  // Subscribe / Unsubscribe
  // ----------------------------------------------------------

  const subscribe = useCallback(
    <T = Record<string, unknown>>(
      eventType: WSEventType,
      handler: WSEventHandler<T>
    ): (() => void) => {
      if (!handlersRef.current.has(eventType)) {
        handlersRef.current.set(eventType, new Set());
      }
      const handlerSet = handlersRef.current.get(eventType)!;
      const wrappedHandler = handler as WSEventHandler<unknown>;
      handlerSet.add(wrappedHandler);

      // Return unsubscribe function
      return () => {
        handlerSet.delete(wrappedHandler);
        if (handlerSet.size === 0) {
          handlersRef.current.delete(eventType);
        }
      };
    },
    []
  );

  // ----------------------------------------------------------
  // Dispatch event to all registered handlers
  // ----------------------------------------------------------

  const dispatch = useCallback(
    (eventType: WSEventType, data: unknown) => {
      const handlerSet = handlersRef.current.get(eventType);
      if (handlerSet) {
        handlerSet.forEach((handler) => {
          try {
            handler(data);
          } catch (err) {
            console.error(
              `[WS] Error in handler for ${eventType}:`,
              err
            );
          }
        });
      }
    },
    []
  );

  // ----------------------------------------------------------
  // Cleanup timers
  // ----------------------------------------------------------

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  // ----------------------------------------------------------
  // Close existing connection
  // ----------------------------------------------------------

  const closeConnection = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close(1000, 'Client disconnect');
      }
      wsRef.current = null;
    }
  }, [clearTimers]);

  // ----------------------------------------------------------
  // Connect
  // ----------------------------------------------------------

  const connect = useCallback(() => {
    if (disabled || isUnmountingRef.current) return;

    const token = getAccessToken();
    if (!token) {
      setConnectionState('disconnected');
      setLastError('No access token available');
      return;
    }

    // Close any existing connection
    closeConnection();
    setConnectionState('connecting');
    setLastError(null);

    const url = buildWebSocketUrl(token, siteCode);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected to planning updates');
        setConnectionState('connected');
        setLastError(null);
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
        reconnectAttemptsRef.current = 0;
        setStats((prev) => ({
          ...prev,
          reconnectAttempts: 0,
          connectedSince: new Date().toISOString(),
        }));

        // Start ping/pong keepalive
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          messageCountRef.current += 1;
          setStats((prev) => ({
            ...prev,
            messagesReceived: messageCountRef.current,
          }));

          // Store notifications for UI display
          if (message.type === 'notification') {
            const notif = message.data as unknown as NotificationData;
            setNotifications((prev) =>
              [notif, ...prev].slice(0, MAX_NOTIFICATIONS)
            );
          }

          // Also store conflict alerts as notifications
          if (message.type === 'conflict_alert') {
            const alert = message.data as unknown as ConflictAlertData;
            setNotifications((prev) =>
              [
                {
                  level: alert.severity,
                  title: `Conflict: ${alert.conflict_type.replace(/_/g, ' ')}`,
                  message: alert.description,
                  timestamp: alert.timestamp,
                },
                ...prev,
              ].slice(0, MAX_NOTIFICATIONS)
            );
          }

          // Dispatch to all subscribed handlers
          dispatch(message.type, message.data);
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onerror = (event: Event) => {
        console.error('[WS] Connection error:', event);
        setLastError('WebSocket connection error');
      };

      ws.onclose = (event: CloseEvent) => {
        clearTimers();

        if (isUnmountingRef.current) {
          setConnectionState('disconnected');
          return;
        }

        // Handle authentication failure (4401 from PlanningConsumer)
        if (event.code === 4401) {
          console.warn('[WS] Authentication failed, attempting token refresh');
          setLastError('Authentication failed');
          setConnectionState('reconnecting');

          // Try to refresh the token and reconnect
          refreshAccessToken().then((success) => {
            if (success && !isUnmountingRef.current) {
              setTimeout(() => connect(), 500);
            } else {
              setConnectionState('disconnected');
              setLastError('Token refresh failed');
            }
          });
          return;
        }

        // Normal close requested by client
        if (event.code === 1000) {
          setConnectionState('disconnected');
          return;
        }

        // Unexpected close — schedule reconnect with backoff
        console.warn(
          `[WS] Connection closed (code: ${event.code}, reason: ${event.reason}). Reconnecting...`
        );
        setConnectionState('reconnecting');
        reconnectAttemptsRef.current += 1;
        setStats((prev) => ({
          ...prev,
          reconnectAttempts: reconnectAttemptsRef.current,
          connectedSince: null,
        }));

        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(
          delay * RECONNECT_BACKOFF_MULTIPLIER,
          MAX_RECONNECT_DELAY
        );

        setLastError(
          `Reconnecting in ${Math.round(delay / 1000)}s (attempt ${reconnectAttemptsRef.current})`
        );

        reconnectTimerRef.current = setTimeout(() => {
          if (!isUnmountingRef.current) {
            connect();
          }
        }, delay);
      };
    } catch (err) {
      console.error('[WS] Failed to create WebSocket:', err);
      setConnectionState('disconnected');
      setLastError('Failed to create WebSocket connection');
    }
  }, [
    disabled,
    getAccessToken,
    refreshAccessToken,
    siteCode,
    closeConnection,
    clearTimers,
    dispatch,
  ]);

  // ----------------------------------------------------------
  // Manual reconnect
  // ----------------------------------------------------------

  const reconnect = useCallback(() => {
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // ----------------------------------------------------------
  // Clear notifications
  // ----------------------------------------------------------

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // ----------------------------------------------------------
  // Auto-connect when authenticated, disconnect on logout
  // ----------------------------------------------------------

  useEffect(() => {
    isUnmountingRef.current = false;

    if (isAuthenticated && !disabled) {
      connect();
    } else {
      closeConnection();
      setConnectionState('disconnected');
    }

    return () => {
      isUnmountingRef.current = true;
      closeConnection();
    };
  }, [isAuthenticated, disabled, connect, closeConnection]);

  // ----------------------------------------------------------
  // Context value
  // ----------------------------------------------------------

  const contextValue: WebSocketContextType = {
    connectionState,
    isConnected: connectionState === 'connected',
    lastError,
    subscribe,
    reconnect,
    notifications,
    clearNotifications,
    stats,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Access the WebSocket context.
 * Must be used within a <WebSocketProvider>.
 */
export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
